import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { ProgressBar } from "@/components/ProgressBar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/auth/AuthContext";
import { useQuery } from "@tanstack/react-query";
import {
  getMyEnrollments,
  getMyProgress,
  formatCourseLevels,
  type UserProgress,
  type Enrollment,
} from "@/api/lms/courses";
import { BookOpen } from "lucide-react";

function toNumberPercent(v: string | undefined | null): number {
  if (v === undefined || v === null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0;
}

function ProgressForCourse({ progress }: { progress: UserProgress | undefined }) {
  const pct = toNumberPercent(progress?.progressPercentage);
  return (
    <div className="space-y-1">
      <ProgressBar progress={pct} />
      <div className="text-xs text-muted-foreground font-semibold">{pct.toFixed(0)}%</div>
    </div>
  );
}

export default function MyCoursesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const enrollmentsQuery = useQuery({
    queryKey: ["myEnrollments"],
    queryFn: getMyEnrollments,
  });

  const progressQuery = useQuery({
    queryKey: ["myProgress"],
    queryFn: getMyProgress,
  });

  const progressByCourseId = useMemo(() => {
    const map = new Map<string, UserProgress>();
    const items = progressQuery.data ?? [];
    for (const p of items) map.set(p.courseId, p);
    return map;
  }, [progressQuery.data]);

  const enrollments: Enrollment[] = enrollmentsQuery.data ?? [];

  const isLoading = enrollmentsQuery.isPending || progressQuery.isPending;

  if (!user) return null;
  if (user.role !== "student") {
    return (
      <DashboardLayout>
        <div className="p-6 md:p-10 max-w-3xl mx-auto">
          <KCard className="p-6">
            <p className="text-muted-foreground">This page is for students.</p>
          </KCard>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="dash-page">
        <div className="dash-page-header mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="dash-page-title">My Courses</h1>
            <p className="dash-page-subtitle">Your enrolled learning paths.</p>
          </div>
          <Button onClick={() => navigate("/")} variant="secondary" size="sm">
            Browse Courses
          </Button>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Skeleton className="h-40 w-full rounded-2xl" />
            <Skeleton className="h-40 w-full rounded-2xl" />
            <Skeleton className="h-40 w-full rounded-2xl" />
          </div>
        ) : enrollments.length === 0 ? (
          <div className="dash-empty-state">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <BookOpen size={24} className="text-muted-foreground" />
            </div>
            <p className="font-semibold text-foreground mb-1 text-center">No enrollments yet</p>
            <p className="text-sm text-muted-foreground mb-6 text-center">Enroll from the landing page to start learning.</p>
            <Button onClick={() => navigate("/")} size="sm" className="mx-auto block">Browse Courses</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {enrollments.map((e) => {
              const course = e.course;
              if (!course) return null;
              const progress = progressByCourseId.get(course.id);
              const pct = toNumberPercent(progress?.progressPercentage);
              const isComplete = pct >= 100;
              return (
                <KCard key={e.id} hoverable className="p-5 flex flex-col gap-3" onClick={() => navigate(`/course?courseId=${course.id}`)}>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground line-clamp-2 leading-snug">{course.title}</h3>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {formatCourseLevels(course)} · {course.type === "LIVE" ? "Live" : "Recorded"}
                    </p>
                  </div>
                  <div className="mt-auto space-y-1.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Progress</span>
                      <span className={`font-semibold ${isComplete ? "text-emerald-600" : "text-foreground"}`}>{pct.toFixed(0)}%</span>
                    </div>
                    <ProgressBar progress={pct} variant={isComplete ? "success" : "primary"} />
                  </div>
                  <Button size="sm" className="w-full mt-1">
                    {pct === 0 ? "Start learning" : isComplete ? "Review course" : "Continue learning"}
                  </Button>
                </KCard>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

